/**
 * `demo/guide/marks.tsx` - the small markup of a guide step's words (#2824).
 *
 * `**Label**` marks the exact on-screen label of a control the visitor
 * presses. It renders as a bold element. No other markup is read, and the
 * text is never parsed as HTML.
 */
import type { ReactNode } from "react";

const MARK = /\*\*([^*]+)\*\*/g;

/** The words with the markers cut out: for measuring, and for labels read aloud. */
export function stripMarks(text: string): string {
  return text.replace(MARK, "$1");
}

/** The words as React nodes, each `**label**` a `<strong>`. */
export function renderMarks(text: string): ReactNode {
  const out: ReactNode[] = [];
  let last = 0;
  for (const hit of text.matchAll(MARK)) {
    const at = hit.index ?? 0;
    if (at > last) out.push(<span key={`t${last}`}>{text.slice(last, at)}</span>);
    out.push(<strong key={at}>{hit[1]}</strong>);
    last = at + hit[0].length;
  }
  if (last < text.length) out.push(<span key={`t${last}`}>{text.slice(last)}</span>);
  return out;
}

interface MakeChangeValues {
  kind: string;
  station?: string | null;
  eligible_stations?: string[] | null;
  required_per_session?: number | null;
  tier?: string | null;
  tier_label?: string | null;
}

/** The exact values of the change the visitor makes: the line under a "Make the change" step. */
export function makeChangeDetail(toMake: MakeChangeValues | null | undefined): string | null {
  if (!toMake) return null;
  const stations = (toMake.eligible_stations ?? []).join(", ");
  if (toMake.kind === "station" && toMake.station && toMake.required_per_session != null)
    return `Set ${toMake.station} to ${toMake.required_per_session} people needed per session.`;
  if (toMake.kind === "new_member")
    return `Set the level to ${toMake.tier_label ?? toMake.tier ?? ""} and tick ${stations}.`;
  if (stations) return `Leave only ${stations} ticked.`;
  return null;
}
